import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Download, Instagram, Mail, ShoppingBag } from "lucide-react";
import { useLang } from "../lib/i18n";
import { API, api } from "../lib/api";
import { amazonBookUrl } from "../lib/amazon";
import { MASCOT_JOURNEY } from "../lib/mascotData";

function getChildName() {
  try {
    const p = JSON.parse(localStorage.getItem("bf_child_profile") || "null");
    return (p?.name || "").trim();
  } catch { return ""; }
}

export default function MascotJourney() {
  const { emotion } = useParams();
  const { lang } = useLang();
  const [mascot, setMascot] = useState(null);
  const cfg = MASCOT_JOURNEY[emotion];

  useEffect(() => {
    api.get(`/mascots?lang=${lang}`).then((r) => {
      setMascot((r.data || []).find((m) => m.id === emotion) || null);
    }).catch(() => {});
  }, [emotion, lang]);

  if (!cfg) return <Navigate to="/" replace />;
  if (!mascot) return <div className="min-h-[40vh] grid place-items-center text-[#8A8D91]">…</div>;

  const isFr = lang === "fr";
  const story = cfg.story;
  const heroText = isFr ? cfg.hero_fr : cfg.hero_en;

  return (
    <div className="space-y-10" data-testid={`mascot-page-${emotion}`}>
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#8A8D91] hover:text-[#3D405B]">
        <ArrowLeft className="w-4 h-4" /> {isFr ? "Retour" : "Back"}
      </Link>

      <header
        className="rounded-3xl border p-8 md:p-12 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${cfg.accent}22, #FDFBF7 55%, ${cfg.accent}18)`, borderColor: `${cfg.accent}55` }}
      >
        <div className="grid md:grid-cols-[280px,1fr] gap-8 items-center relative">
          <img src={`/covers/${emotion}.jpg`} alt="" className="rounded-2xl shadow-2xl w-full max-w-[280px] mx-auto md:mx-0" />
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-[10px] font-semibold uppercase tracking-widest" style={{ background: cfg.accent }}>
              <Sparkles className="w-3 h-3" /> {mascot.emotion}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B] mt-3">{mascot.name} — {mascot.animal}</h1>
            <p className="text-lg text-[#3D405B]/80 mt-3 leading-relaxed">{heroText}</p>
            <a href={amazonBookUrl(lang)} target="_blank" rel="noopener noreferrer"
               data-testid="mascot-amazon-link"
               className="inline-flex items-center gap-2 mt-5 bg-[#3D405B] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2f3147] transition-colors">
              {isFr ? "Découvrir le livre sur Amazon" : "Discover the book on Amazon"} →
            </a>
          </div>
        </div>
      </header>

      {story && (
        <section data-testid="mascot-story">
          <h2 className="font-heading text-3xl text-[#3D405B]">{isFr ? "L'histoire" : "The story"}</h2>
          <p className="text-[#8A8D91] mt-1 mb-6">{isFr ? "3 moments-clés du livre." : "3 key moments from the book."}</p>
          <div className="grid md:grid-cols-3 gap-5">
            {story.map((s, i) => (
              <article key={i} className="bg-white rounded-3xl border border-[#E8E6E1] overflow-hidden">
                <img src={s.img} alt="" className="w-full aspect-[2/1] object-cover" loading="lazy" />
                <div className="p-5">
                  <h3 className="font-heading text-lg text-[#3D405B]">{isFr ? s.title_fr : s.title_en}</h3>
                  <p className="text-sm text-[#3D405B]/80 mt-2 leading-relaxed">{isFr ? s.text_fr : s.text_en}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-dashed p-4 text-center text-sm text-[#3D405B]/80"
               style={{ borderColor: `${cfg.accent}88`, background: `${cfg.accent}0F` }}
               data-testid="mascot-story-teaser">
            {isFr
              ? <>La suite (le journal de 10 jours et le kit du dompteur) se découvre dans le livre 📖 —
                  <a href={amazonBookUrl(lang)} target="_blank" rel="noopener noreferrer"
                     className="underline font-semibold ml-1" style={{ color: cfg.accent }}>
                    l'attraper sur Amazon
                  </a></>
              : <>The rest (the 10-day journal & the tamer's kit) lives inside the book 📖 —
                  <a href={amazonBookUrl(lang)} target="_blank" rel="noopener noreferrer"
                     className="underline font-semibold ml-1" style={{ color: cfg.accent }}>
                    grab it on Amazon
                  </a></>}
          </div>
        </section>
      )}

      <section className="rounded-3xl p-6 md:p-8 text-center border" data-testid="mascot-diploma"
               style={{ background: `linear-gradient(90deg, ${cfg.accent}22, ${cfg.accent}11)`, borderColor: `${cfg.accent}55` }}>
        <div className="text-5xl">🏆</div>
        <h3 className="font-heading text-2xl md:text-3xl text-[#3D405B] mt-2">
          {isFr ? `Diplôme officiel de ${mascot.name}` : `${mascot.name}'s official diploma`}
        </h3>
        <DiplomaDownload lang={lang} emotion={emotion} accent={cfg.accent} />
      </section>

      <FloatingBuyButton accent={cfg.accent} isFr={isFr} lang={lang} />
    </div>
  );
}

function FloatingBuyButton({ accent, isFr, lang }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 380);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <a
      href={amazonBookUrl(lang)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-buy-btn"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={`fixed z-40 bottom-4 left-4 md:bottom-6 md:left-6 inline-flex items-center gap-2 rounded-full pl-3 pr-4 md:pl-4 md:pr-5 py-2.5 md:py-3 text-white text-sm font-semibold shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-[0_12px_40px_-8px_rgba(0,0,0,0.4)] ${
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      style={{ background: accent }}
    >
      <span className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white/25 grid place-items-center">
        <ShoppingBag className="w-4 h-4" />
      </span>
      <span className="hidden sm:inline">{isFr ? "Acheter le livre" : "Buy the book"}</span>
      <span className="sm:hidden">{isFr ? "Le livre" : "The book"}</span>
    </a>
  );
}

function DiplomaDownload({ lang, emotion, accent }) {
  const [name, setName] = useState(() => getChildName());
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const isFr = lang === "fr";

  const download = async (format) => {
    const child = (name || "").trim();
    if (!child) return;
    const setter = format === "story" ? setSharing : setBusy;
    setter(true);
    try {
      const res = await fetch(`${API}/certificate/sparky?name=${encodeURIComponent(child)}&lang=${lang}&emotion=${emotion}&format=${format}`);
      if (!res.ok) throw new Error(format);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "story" ? "jpg" : "pdf";
      a.download = `${isFr ? "diplome" : "diploma"}-${emotion}-${child}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally { setter(false); }
  };

  return (
    <div className="mt-5 max-w-md mx-auto space-y-3" data-testid="diploma-download">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)}
             placeholder={isFr ? "Prénom de l'enfant" : "Child's first name"}
             data-testid="diploma-name-input" maxLength={40}
             className="w-full bg-white border border-[#E8E6E1] rounded-full px-5 py-2.5 focus:outline-none focus:ring-2 text-[#3D405B] text-center"
             style={{ "--tw-ring-color": `${accent}55`, borderColor: name ? accent : "#E8E6E1" }} />
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={() => download("pdf")} disabled={!name.trim() || busy}
                data-testid="diploma-download-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ background: accent }}>
          <Download className="w-4 h-4" /> {busy ? (isFr ? "Préparation…" : "Preparing…") : (isFr ? "Télécharger (PDF)" : "Download (PDF)")}
        </button>
        <button onClick={() => download("story")} disabled={!name.trim() || sharing}
                data-testid="diploma-share-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#3D405B] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#2E3049] transition-colors disabled:opacity-50">
          <Instagram className="w-4 h-4" /> {sharing ? (isFr ? "Génération…" : "Generating…") : (isFr ? "Partager en story" : "Share as story")}
        </button>
      </div>
      <div className="mt-4 rounded-2xl bg-white/70 border p-4 text-center" style={{ borderColor: `${accent}55` }} data-testid="ugc-invite">
        <div className="text-2xl">📸</div>
        <p className="text-sm text-[#3D405B] font-semibold mt-1 leading-snug">
          {isFr ? "Envoyez-nous la photo de votre pilote avec son diplôme" : "Send us the photo of your child with their diploma"}
        </p>
        <p className="text-[13px] text-[#3D405B]/75 mt-1 leading-relaxed">
          {isFr ? "On la republie avec grand plaisir ❤️" : "We'll gladly repost it ❤️"}
        </p>
        <div className="mt-3 flex flex-col sm:flex-row items-center justify-center gap-2 text-[12px]">
          <a href="https://www.instagram.com/legangdesemotions/" target="_blank" rel="noopener noreferrer"
             data-testid="ugc-instagram"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#E07A5F] to-[#F4A261] text-white font-semibold hover:opacity-90 transition-opacity">
            <Instagram className="w-3.5 h-3.5" /> {isFr ? "Taguer @legangdesemotions" : "Tag @legangdesemotions"}
          </a>
          <span className="text-[#8A8D91]">{isFr ? "ou" : "or"}</span>
          <a href="mailto:bonjour@legangdesemotions.fr?subject=Photo%20de%20mon%20pilote%20%F0%9F%A6%8A"
             data-testid="ugc-email"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E8E6E1] text-[#3D405B] font-semibold hover:border-[#F4A261] transition-colors">
            <Mail className="w-3.5 h-3.5" /> {isFr ? "Par email" : "By email"}
          </a>
        </div>
      </div>
    </div>
  );
}
